import type React from "react"
import { useEffect, useRef, useCallback } from "react"
import { useAppDispatch, useAppSelector } from "@/app/hooks"
import {
  fetchPosts,
  selectAllPosts,
  selectPostsStatus,
  selectCurrentTags,
  selectHasMore,
  selectLastId,
  selectTags,
} from "@/features/posts/postsSlice"
import { PostItem } from "./PostItem"
import "./PostList.css"
import { useMemo } from "react"

export const PostList: React.FC = () => {
  const dispatch = useAppDispatch()
  const posts = useAppSelector(selectAllPosts)
  const status = useAppSelector(selectPostsStatus)
  const currentTags = useAppSelector(selectCurrentTags)
  const hasMore = useAppSelector(selectHasMore)
  const lastId = useAppSelector(selectLastId)
  const allTags = useAppSelector(selectTags)
  
  const tagMap = useMemo(() => {
    const map: Record<string, string> = {}
    allTags.forEach(tag => {
      map[tag.name] = tag.title
    })
    return map
  }, [allTags])

  const observer = useRef<IntersectionObserver | null>(null)
  
  // Last element ref for infinite scroll
  const lastPostElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (status === "loading") return
      if (observer.current) observer.current.disconnect()
      
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          dispatch(fetchPosts({ tags: currentTags, lastId }))
        }
      })
      
      if (node) observer.current.observe(node)
    },
    [status, hasMore, dispatch, currentTags, lastId],
  )

  useEffect(() => {
    // Initial fetch if empty and idle
    if (status === "idle" && posts.length === 0) {
        dispatch(fetchPosts({ tags: currentTags, lastId: undefined }))
    }
  }, [dispatch, status, posts.length, currentTags])

  return (
    <div className="post-list-container">
      {posts.map((post, index) => {
        if (posts.length === index + 1) {
          return (
            <div ref={lastPostElementRef} key={`${post.id}-${index}`}>
              <PostItem post={post} tagMap={tagMap} />
            </div>
          )
        } else {
          return <PostItem key={`${post.id}-${index}`} post={post} tagMap={tagMap} />
        }
      })}
      
      {status === "loading" && (
        <div className="post-list-loading">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {status === "succeeded" && posts.length === 0 && (
          <div className="post-list-empty">
              게시글이 없습니다.
          </div>
      )}
      
       {status === "failed" && (
          <div className="post-list-error">
              게시글을 불러오는 중 오류가 발생했습니다.
          </div>
      )}
    </div>
  )
}
